import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Check, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { uploadPDF } from '../services/api';
import { toast } from 'sonner';

const PDFUploader = ({ onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const response = await uploadPDF(file);
      setUploadResult(response.data);
      toast.success(`${response.data.prices_extracted} prix extraits, ${response.data.alerts_triggered} alertes déclenchées`);
      onUploadSuccess?.(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'upload';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  }, [onUploadSuccess]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    disabled: uploading
  });

  return (
    <div className="bg-white border border-[#BFC6CC]/30 rounded-xl p-6 shadow-sm">
      <h3 className="text-lg font-bold text-[#191D1E] mb-4">Upload Newsletter PDF</h3>
      
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-8 md:p-12 text-center transition-all cursor-pointer ${
          isDragActive
            ? 'border-[#397DFF] bg-[#397DFF]/5'
            : uploading
            ? 'border-[#BFC6CC] bg-[#F5F6F9] cursor-wait'
            : 'border-[#BFC6CC] hover:border-[#397DFF] hover:bg-[#397DFF]/5 bg-[#F5F6F9]'
        }`}
        data-testid="pdf-dropzone"
      >
        <input {...getInputProps()} data-testid="pdf-input" />
        
        <AnimatePresence mode="wait">
          {uploading ? (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center"
            >
              <Loader2 className="w-12 h-12 text-[#397DFF] animate-spin mb-4" />
              <p className="text-[#191D1E] font-medium">Extraction des prix en cours...</p>
              <p className="text-sm text-[#687175] mt-1">Analyse du document avec l'IA</p>
            </motion.div>
          ) : uploadResult ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center"
            >
              <div className="w-12 h-12 rounded-full bg-[#10D183]/10 flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-[#10D183]" />
              </div>
              <p className="text-[#10D183] font-medium">Upload réussi !</p>
              <p className="text-sm text-[#687175] mt-1">
                {uploadResult.prices_extracted} prix extraits • {uploadResult.alerts_triggered} alertes déclenchées
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadResult(null);
                }}
                className="mt-4 text-sm text-[#397DFF] hover:text-[#2563eb]"
              >
                Uploader un autre fichier
              </button>
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center"
            >
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-red-500 font-medium">Erreur</p>
              <p className="text-sm text-[#687175] mt-1">{error}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setError(null);
                }}
                className="mt-4 text-sm text-[#397DFF] hover:text-[#2563eb]"
              >
                Réessayer
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                isDragActive ? 'bg-[#397DFF]/10' : 'bg-white border border-[#BFC6CC]'
              }`}>
                {isDragActive ? (
                  <FileText className="w-8 h-8 text-[#397DFF]" />
                ) : (
                  <Upload className="w-8 h-8 text-[#687175]" />
                )}
              </div>
              <p className="text-[#191D1E] font-medium">
                {isDragActive ? 'Déposez le fichier ici' : 'Glissez-déposez votre PDF'}
              </p>
              <p className="text-sm text-[#687175] mt-1">ou cliquez pour sélectionner</p>
              <p className="text-xs text-[#939BA0] mt-3">Newsletter Place des Energies (PDF)</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {uploadResult?.data && (
        <div className="mt-6 space-y-4">
          {uploadResult.data.analysis && (
            <div className="p-4 bg-[#F5F6F9] rounded-lg">
              <h4 className="text-sm font-medium text-[#191D1E] mb-2">Analyse du marché</h4>
              {uploadResult.data.analysis.electricity_summary && (
                <p className="text-sm text-[#687175] mb-2">
                  <strong className="text-[#F9C80E]">Électricité:</strong> {uploadResult.data.analysis.electricity_summary}
                </p>
              )}
              {uploadResult.data.analysis.gas_summary && (
                <p className="text-sm text-[#687175]">
                  <strong className="text-[#397DFF]">Gaz:</strong> {uploadResult.data.analysis.gas_summary}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PDFUploader;
